import { Component, Renderer2 } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { PATHS } from 'src-ironside/app-routing.module';
import { mapToArray } from 'src-ironside/helpers/convert-map-to-array';
import { Category, CategoryRepository } from 'src-ironside/repositories/category.repository';
import { AccueilDataService } from 'src-ironside/services/accueil-data.service';
import { ScreenMediator } from 'src-ironside/services/screen.mediator';
import { SessionStorageService } from 'src-ironside/services/session-storage.service';
import { BreadCrumbItem } from 'src-ironside/shared/components/bread-crumb/bread-crumb.component';
import {
    buildCategoryHierarchyRandom,
    flatCategories,
    sortCategories,
} from 'src-ironside/utils/categories';
import { ProductCatalogDialogComponent } from './product-catalog-dialog/product-catalog-dialog.component';
import { Subject, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';

export type SelectedCategory = Category & {
    selected: boolean;
    subCategory?: SelectedCategory[];
};

@Component({
    selector: 'app-product-catalog',
    templateUrl: './product-catalog.component.html',
    styleUrls: ['./product-catalog.component.css'],
    providers: [CategoryRepository],
})
export class ProductCatalogComponent {
    readonly unsubscribe$ = new Subject();

    categories: SelectedCategory[] = [];
    catalogs: Category[] = [];
    currentLang =
        environment.languagesExtranet.find((lang) => lang.code === this.translate.currentLang)
            ?.locale || 'en_GB';
    constructor(
        public router: Router,
        private renderer: Renderer2,
        private categoryRepository: CategoryRepository,
        private dialog: MatDialog,
        private accueilDataService: AccueilDataService,
        private screenMediator: ScreenMediator,
        private sessionStorageService: SessionStorageService,
        private translate: TranslateService
    ) {}

    ngOnInit(): void {
        const body = document.querySelector('body');
        this.renderer.removeClass(body, 'overlay');

        this.categoryRepository
            .findAllCategoryLevel(3)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((res) => {
                const result = buildCategoryHierarchyRandom(res);
                this.catalogs = sortCategories(result, this.currentLang);
            });
    }

    get breadCrumb() {
        let breadCrumbs: BreadCrumbItem[] = [
            { name: this.translate.instant('Menu.home'), url: PATHS.home_en },
            { name: this.translate.instant('Menu.product'), isEnable: false },
            { name: this.translate.instant('Menu.products-catalog'), isEnable: false },
        ];
        return breadCrumbs;
    }

    showDialog() {
        this.dialog
            .open(ProductCatalogDialogComponent, {
                data: {
                    categories: JSON.parse(JSON.stringify(this.catalogs)),
                },
            })
            .afterClosed()
            .pipe(takeUntil(this.unsubscribe$))

            .subscribe((result) => {
                if (result) {
                    this.catalogs = result;
                }
            });
    }

    get categoryMapToArray() {
        return mapToArray;
    }

    categoryToSelectedCategory(cat: Category): SelectedCategory {
        return {
            ...cat,
            selected: false,
            subCategory: cat?.subCategory
                ? cat?.subCategory.map((subCat) => this.categoryToSelectedCategory(subCat))
                : undefined,
        };
    }

    goToSearchPage(category: Category) {
        const categoryIds: string[] = [category?.id!];

        function findCategory(category: Category) {
            category.subcategoryList.forEach((el: any) => {
                categoryIds.push(el?.id!);
                if (el?.subcategoryList?.length > 0) {
                    findCategory(el);
                }
            });
        }
        findCategory(category);

        if (category) {
            this.accueilDataService.filterData = {
                categoryName: category?.nameCategory,
                categoryList: [category],
            };

            this.sessionStorageService.setFilters({
                isCategoryModal: false,
                categoryList: [category.id],
                catalogName: category?.nameCategory,
                categories: categoryIds,
            });
            this.screenMediator.toRecherche();
        }
    }

    findNodeByCode(tree: Category[], code: string): any | null {
        if (!tree) return null;

        for (const node of tree) {
            if (node?.codeCategory === code) {
                return node;
            }

            if (node?.subcategoryList) {
                const foundNode = this.findNodeByCode(node.subcategoryList, code);
                if (foundNode) {
                    return foundNode;
                }
            }
        }

        return null;
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }
}
